import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import ReportSelector from './components/ReportSelector';
import ReportFilters from './components/ReportFilters';
import ReportViewer from './components/ReportViewer';
import SavedReports from './components/SavedReports';
import DrillDownModal from './components/DrillDownModal';
import CustomReportBuilder from './components/CustomReportBuilder';
import Button from '../../components/ui/Button';
import { useReports } from '../../hooks/useReports';

const FinancialReports = () => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [drillDownData, setDrillDownData] = useState(null);
  const [showDrillDown, setShowDrillDown] = useState(false);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const [filters, setFilters] = useState({
    period: 'current_month',
    comparison: 'none',
    startDate: '',
    endDate: '',
    department: 'all',
    project: 'all'
  });

  const {
    loading,
    error,
    generatedReport,
    savedReports,
    generateReport,
    getAccountTransactions,
    loadSavedReports,
    loadSavedReport,
    saveCustomReport,
    deleteSavedReport,
    exportReport,
    clearError,
  } = useReports();

  // Load saved reports on component mount
  useEffect(() => {
    loadSavedReports().catch(console.error);
  }, [loadSavedReports]);

  const reportTypes = [
    {
      id: 'profit_loss',
      name: 'Profit & Loss Statement',
      icon: 'TrendingUp',
      description: 'Income statement showing revenue, expenses, and net profit for the selected period'
    },
    {
      id: 'balance_sheet',
      name: 'Balance Sheet',
      icon: 'Scale',
      description: 'Financial position showing assets, liabilities, and equity at a specific point in time'
    },
    {
      id: 'cash_flow',
      name: 'Cash Flow Statement',
      icon: 'DollarSign',
      description: 'Cash inflows and outflows from operating, investing, and financing activities'
    },
    {
      id: 'trial_balance',
      name: 'Trial Balance',
      icon: 'ListChecks',
      description: 'List of all general ledger accounts with their debit and credit balances'
    }
  ];

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      period: 'current_month',
      comparison: 'none',
      startDate: '',
      endDate: '',
      department: 'all',
      project: 'all'
    });
  };

  const handleGenerateReport = async () => {
    if (!selectedReport) return;

    try {
      clearError();
      await generateReport(selectedReport.id, filters);
    } catch (error) {
      console.error('Failed to generate report:', error);
      // Error is handled by the hook
    }
  };

  const handleDrillDown = async (row) => {
    if (!row?.accountId) {
      console.warn('No account ID available for drill-down');
      return;
    }

    try {
      const transactions = await getAccountTransactions(row.accountId, {
        startDate: filters.startDate,
        endDate: filters.endDate,
        period: filters.period
      });

      setDrillDownData({
        account: row.account,
        amount: row.amount,
        transactions: transactions || []
      });
      setShowDrillDown(true);
    } catch (error) {
      console.error('Failed to get account transactions:', error);
      // Fallback to mock data for demo purposes
      const mockTransactions = {
        account: row?.account,
        amount: row?.amount,
        transactions: [
          { date: '01/05/2026', description: 'Invoice #INV-2026-001 - Acme Corp', reference: 'INV-2026-001', debit: '$12,450.00', credit: '' },
          { date: '01/08/2026', description: 'Invoice #INV-2026-015 - Tech Solutions Inc', reference: 'INV-2026-015', debit: '$8,920.00', credit: '' },
          { date: '01/12/2026', description: 'Invoice #INV-2026-028 - Global Industries', reference: 'INV-2026-028', debit: '$15,680.00', credit: '' },
        ]
      };
      setDrillDownData(mockTransactions);
      setShowDrillDown(true);
    }
  };

  const handleExport = async (format) => {
    if (!generatedReport) return;

    try {
      await exportReport(generatedReport, format);
    } catch (error) {
      console.error('Failed to export report:', error);
    }
  };

  const handleLoadSavedReport = async (report) => {
    try {
      await loadSavedReport(report.id);
    } catch (error) {
      console.error('Failed to load saved report:', error);
    }
  };

  const handleDeleteSavedReport = async (report) => {
    try {
      await deleteSavedReport(report.id);
    } catch (error) {
      console.error('Failed to delete saved report:', error);
    }
  };

  const handleShareSavedReport = (report) => {
    // TODO: Implement sharing functionality
    console.log('Sharing saved report:', report?.name);
  };

  const handleSaveCustomReport = async (reportData) => {
    try {
      await saveCustomReport(reportData);
      setShowCustomBuilder(false);
    } catch (error) {
      console.error('Failed to save custom report:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-16">
        <div className="max-w-[1920px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground mb-2">
                  Financial Reports
                </h1>
                <p className="font-body text-sm md:text-base text-muted-foreground">
                  Generate comprehensive financial statements and custom analytics for your business
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowCustomBuilder(true)}
                iconName="Wrench"
                iconPosition="left"
              >
                Custom Report Builder
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-1 space-y-6 md:space-y-8">
              <ReportSelector
                selectedReport={selectedReport}
                onReportChange={setSelectedReport}
                reportTypes={reportTypes}
              />

              <ReportFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onGenerate={handleGenerateReport}
                onReset={handleResetFilters}
                loading={loading}
                disabled={!selectedReport}
              />

              <SavedReports
                savedReports={savedReports}
                onLoad={handleLoadSavedReport}
                onDelete={handleDeleteSavedReport}
                onShare={handleShareSavedReport}
              />
            </div>

            <div className="lg:col-span-2">
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm">{error}</p>
                  <button 
                    onClick={clearError}
                    className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              <ReportViewer
                report={generatedReport}
                onExport={handleExport}
                onDrillDown={handleDrillDown}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </main>

      <QuickActionToolbar />

      <DrillDownModal
        isOpen={showDrillDown}
        onClose={() => setShowDrillDown(false)}
        data={drillDownData}
      />

      <CustomReportBuilder
        isOpen={showCustomBuilder}
        onClose={() => setShowCustomBuilder(false)}
        onSave={handleSaveCustomReport}
      />
    </div>
  );
};

export default FinancialReports;